// google-auth.service.ts
import { Injectable } from '@angular/core';
import { AuthRsocialService } from './auth-rsocial.service';

declare const google: any;

@Injectable({
    providedIn: 'root'
})
export class GoogleAuthService {
    private isScriptLoaded = false;
    private isInitialized = false;
    private clientId: string | null = null;
    private callback: ((response: any) => void) | null = null;

    constructor(private authService: AuthRsocialService) { }

    // Vérifier si le script Google est déjà chargé
    private isGoogleScriptLoaded(): boolean {
        return typeof window !== 'undefined' &&
            typeof (window as any).google !== 'undefined' &&
            (window as any).google.accounts &&
            (window as any).google.accounts.id;
    }

    // Injecter le script Google dynamiquement
    private loadGoogleScript(): Promise<boolean> {
        return new Promise((resolve, reject) => {
            // Vérifier si déjà chargé
            if (this.isGoogleScriptLoaded()) {
                // console.log('Google script déjà chargé');
                resolve(true);
                return;
            }

            // Vérifier si le script est déjà en cours de chargement
            const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]') as HTMLScriptElement;
            if (existingScript) {
                // console.log('Google script en cours de chargement...');
                existingScript.onload = () => resolve(true);
                existingScript.onerror = () => reject(new Error('Erreur de chargement du script Google'));
                return;
            }

            // Créer et injecter le script
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;

            script.onload = () => {
                // console.log('Google script chargé avec succès');
                this.isScriptLoaded = true;
                resolve(true);
            };

            script.onerror = () => {
                console.error('Erreur lors du chargement du script Google');
                reject(new Error('Impossible de charger le script Google'));
            };

            document.head.appendChild(script);
        });
    }

    // Attendre que l'API Google soit disponible
    private waitForGoogleAPI(): Promise<boolean> {
        return new Promise((resolve) => {
            const checkAPI = () => {
                if (this.isGoogleScriptLoaded()) {
                    resolve(true);
                } else {
                    setTimeout(checkAPI, 100);
                }
            };
            checkAPI();
        });
    }

    // Initialiser Google Sign-In
    async initialize(clientId: string, callback: (response: any) => void): Promise<void> {
        try {
            // Charger le script si nécessaire
            await this.loadGoogleScript();

            // Attendre que google.accounts.id soit disponible
            await this.waitForGoogleAPI();

            this.clientId = clientId;
            this.callback = callback;

            // Initialiser Google Sign-In
            google.accounts.id.initialize({
                client_id: clientId,
                callback: callback
            });

            this.isInitialized = true;
            // console.log('Google Sign-In initialisé');

        } catch (error) {
            console.error('Erreur lors de l\'initialisation:', error);
            throw error;
        }
    }

    // Rendre le bouton de connexion
    renderButton(elementId: string, options: any = {}): void {
        if (!this.isInitialized) {
            console.error('Google Sign-In non initialisé');
            return;
        }

        const defaultOptions = {
            // theme: "outline",
            // size: "large",
            // type: "standard",
            // text: "signin_with"
        };

        const buttonOptions = { ...defaultOptions, ...options };

        const element = document.getElementById(elementId);
        if (element) {
            google.accounts.id.renderButton(element, buttonOptions);
        } else {
            console.error(`Élément avec l'ID '${elementId}' non trouvé`);
        }
    }

    // Afficher la popup de connexion
    prompt(): void {
        if (!this.isInitialized) {
            console.error('Google Sign-In non initialisé');
            return;
        }
        google.accounts.id.prompt();
    }

    // Décoder le JWT token
    decodeJWT(token: string): any {
        try {
            const base64Url = token.split('.')[1];
            const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                    .join('')
            );
            return JSON.parse(jsonPayload);
        } catch (error) {
            console.error('Erreur lors du décodage du JWT:', error);
            return null;
        }
    }

    // Se déconnecter
    signOut(): void {
        // console.log("logout");
        if (this.isGoogleScriptLoaded()) {
            google.accounts.id.disableAutoSelect();
        }
        this.authService.logout();
    }
}